import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Globe, PackageOpen, Store } from 'lucide-react'
import { menuControlApi, plansApi } from '../../lib/api'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import LoadingState from '../../components/shared/LoadingState'
import type { MenuVisibility } from '../../types'
import NoticeBanner from '../../components/shared/NoticeBanner'
import { tx } from '../../lib/i18n'

const MENU_KEYS = [
  'dashboard', 'appointments', 'pos', 'customers', 'staff', 'branches',
  'reports', 'memberships', 'inventory', 'marketing', 'notifications',
  'settings', 'ai_center', 'loyalty', 'gift_cards', 'online_booking',
]

const VISIBILITY_OPTIONS: { value: MenuVisibility; label: string; color: string }[] = [
  { value: 'visible', label: tx('Visible'), color: 'bg-green-100 text-green-700' },
  { value: 'hidden', label: tx('Hidden'), color: 'bg-gray-100 text-gray-600' },
  { value: 'locked_upgrade', label: tx('Locked (Upgrade)'), color: 'bg-purple-100 text-purple-700' },
  { value: 'disabled_maintenance', label: tx('Maintenance'), color: 'bg-orange-100 text-orange-700' },
]

const DEFAULT_CONFIG: Record<string, MenuVisibility> = Object.fromEntries(MENU_KEYS.map((k) => [k, 'visible']))

const MenuGrid: React.FC<{
  config: Record<string, MenuVisibility>
  onChange: (key: string, val: MenuVisibility) => void
}> = ({ config, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {MENU_KEYS.map((key) => {
      const current = config[key] ?? 'visible'
      const opt = VISIBILITY_OPTIONS.find((o) => o.value === current)
      return (
        <div key={key} className="flex items-center justify-between bg-gray-50 rounded-xl p-3 border border-gray-100">
          <div>
            <p className="text-sm font-medium text-gray-800 capitalize">{key.replace(/_/g, ' ')}</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${opt?.color ?? ''}`}>{opt?.label}</span>
          </div>
          <select
            value={current}
            onChange={(e) => onChange(key, e.target.value as MenuVisibility)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ml-2"
          >
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )
    })}
  </div>
)

const MenuControlPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [activeSection, setActiveSection] = useState<'global' | 'plans' | 'shops'>('global')
  const [activePlan, setActivePlan] = useState('starter')
  const [shopId, setShopId] = useState('')
  const [shopSearched, setShopSearched] = useState(false)

  const [globalConfig, setGlobalConfig] = useState<Record<string, MenuVisibility>>(DEFAULT_CONFIG)
  const [planConfigs, setPlanConfigs] = useState<Record<string, Record<string, MenuVisibility>>>({})
  const [shopConfig, setShopConfig] = useState<Record<string, MenuVisibility>>(DEFAULT_CONFIG)

  // Real data: plan catalog (menu configs are keyed by plan id) + saved configs.
  const { data: plans = [] } = useQuery({ queryKey: ['plans'], queryFn: () => plansApi.list().then((r) => r.data.data ?? []) })
  const PLANS = useMemo(() => plans.map((p) => p.name), [plans])
  const planIdOf = (name: string) => plans.find((p) => p.name === name)?.id ?? ''
  const { data: savedGlobal, isLoading: loadingGlobal } = useQuery({
    queryKey: ['menu-global'],
    queryFn: () => menuControlApi.getGlobal().then((r) => r.data.data ?? []),
  })
  const { data: savedPlans } = useQuery({
    queryKey: ['menu-plans'],
    queryFn: () => menuControlApi.getPlanConfigs().then((r) => r.data.data ?? []),
  })
  useEffect(() => {
    const g = savedGlobal?.[0]?.config as Record<string, MenuVisibility> | undefined
    if (g) setGlobalConfig({ ...DEFAULT_CONFIG, ...g })
  }, [savedGlobal])
  useEffect(() => {
    if (!savedPlans || plans.length === 0) return
    const byName: Record<string, Record<string, MenuVisibility>> = {}
    for (const p of plans) {
      const row = savedPlans.find((c) => c.planId === p.id)
      byName[p.name] = { ...globalConfig, ...((row?.config as Record<string, MenuVisibility>) ?? {}) }
    }
    setPlanConfigs(byName)
    if (!byName[activePlan] && plans[0]) setActivePlan(plans[0].name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPlans, plans])

  // The grid is shown only after the shop's config has arrived — otherwise a change made
  // while the request is in flight is silently overwritten when it returns.
  const [shopLoading, setShopLoading] = useState(false)
  const loadShop = async () => {
    const id = shopId.trim()
    if (!id) return
    setShopLoading(true)
    setShopSearched(false)
    try {
      const rows = (await menuControlApi.getShopConfig(id)).data.data ?? []
      setShopConfig({ ...globalConfig, ...((rows[0]?.config as Record<string, MenuVisibility>) ?? {}) })
    } catch {
      setShopConfig(globalConfig)
    } finally {
      setShopLoading(false)
      setShopSearched(true)
    }
  }

  const updateGlobalMutation = useMutation({
    mutationFn: () => menuControlApi.updateGlobal(globalConfig),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['menu-global'] }); showToast({ type: 'success', message: tx('Global config saved.') }) },
    onError: () => showToast({ type: 'error', message: tx('Failed to save.') }),
  })

  const updatePlanMutation = useMutation({
    mutationFn: () => menuControlApi.updatePlanConfig(planIdOf(activePlan), planConfigs[activePlan] ?? {}),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['menu-plans'] }); showToast({ type: 'success', message: `Plan config saved for ${activePlan}.` }) },
    onError: () => showToast({ type: 'error', message: tx('Failed to save.') }),
  })

  const updateShopMutation = useMutation({
    mutationFn: () => menuControlApi.updateShopConfig(shopId.trim(), shopConfig),
    onSuccess: () => showToast({ type: 'success', message: tx('Shop override saved.') }),
    onError: () => showToast({ type: 'error', message: tx('Failed to save.') }),
  })

  const handleGlobalChange = (key: string, val: MenuVisibility) =>
    setGlobalConfig((c) => ({ ...c, [key]: val }))

  const handlePlanChange = (key: string, val: MenuVisibility) =>
    setPlanConfigs((c) => ({ ...c, [activePlan]: { ...(c[activePlan] ?? {}), [key]: val } }))

  const handleShopChange = (key: string, val: MenuVisibility) =>
    setShopConfig((c) => ({ ...c, [key]: val }))

  const sections = [
    { key: 'global', label: tx('Global Config'), icon: Globe },
    { key: 'plans', label: tx('Plan Configs'), icon: PackageOpen },
    { key: 'shops', label: tx('Shop Overrides'), icon: Store },
  ] as const

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Menu Control')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Configure menu visibility globally, per plan, or per shop')}</p>
      </div>

      <NoticeBanner>
        ค่าที่ตั้งในหน้านี้ถูกบันทึกแล้ว แต่ <b>แอปร้าน (หลังร้านเว็บ / แอปมือถือ) ยังไม่ได้อ่านค่านี้</b> — เมนูของร้านยังเปิด/ล็อกตามแพ็กเกจในโค้ดของ queza-core
      </NoticeBanner>

      {/* Section tabs */}
      <div className="flex gap-2">
        {sections.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all ${
              activeSection === key
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Global */}
      {activeSection === 'global' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">{tx('Global Menu Configuration')}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{tx('Default visibility for all shops (overridden by plan/shop configs)')}</p>
            </div>
            {can('menu-control.update') && (
            <button
              onClick={() => updateGlobalMutation.mutate()}
              disabled={updateGlobalMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Save className="h-4 w-4" />
              {updateGlobalMutation.isPending ? tx('Saving...') : 'Save Global Config'}
            </button>
            )}
          </div>
          <MenuGrid config={globalConfig} onChange={handleGlobalChange} />
        </div>
      )}

      {/* Plans */}
      {activeSection === 'plans' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">{tx('Plan Menu Configuration')}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{tx('Customize menu visibility per subscription plan')}</p>
            </div>
            {can('menu-control.update') && (
            <button
              onClick={() => updatePlanMutation.mutate()}
              disabled={updatePlanMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Save className="h-4 w-4" />
              {updatePlanMutation.isPending ? tx('Saving...') : `Save ${activePlan} Config`}
            </button>
            )}
          </div>
          {/* Plan tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {PLANS.map((p) => (
              <button
                key={p}
                onClick={() => setActivePlan(p)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${activePlan === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {p}
              </button>
            ))}
          </div>
          <MenuGrid
            config={planConfigs[activePlan] ?? globalConfig}
            onChange={handlePlanChange}
          />
        </div>
      )}

      {/* Shops */}
      {activeSection === 'shops' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{tx('Shop-Level Menu Overrides')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{tx('Override menu config for a specific shop')}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder={tx('Enter Shop ID (e.g. shop-123)')}
              value={shopId}
              onChange={(e) => { setShopId(e.target.value); setShopSearched(false) }}
              className="flex-1 max-w-xs border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={loadShop}
              disabled={shopLoading || !shopId.trim()}
              className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              {shopLoading ? tx('Loading…') : tx('Load Config')}
            </button>
          </div>
          {shopSearched && shopId && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">{tx('Editing config for shop:')} <span className="font-mono font-medium">{shopId}</span></p>
                {can('menu-control.update') && (
                <button
                  onClick={() => updateShopMutation.mutate()}
                  disabled={updateShopMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="h-4 w-4" />
                  {updateShopMutation.isPending ? tx('Saving...') : 'Save Override'}
                </button>
                )}
              </div>
              <MenuGrid config={shopConfig} onChange={handleShopChange} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default MenuControlPage
